import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";

import Select from "react-select";
import { Col, Row, Button, Label, Input } from "reactstrap";
import { textTranslate } from "../../../utils/i18n";
import API_ENDPOINTS from "../../../configs/API_ENDPOINTS";

const DeviceFiltering = (props) => {
  const {
    organizations,
    deviceModels,
    deviceTypeCategories,
    enableSearch,
    history,
    location,
    enableUrlPersistence = true,
  } = props;
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedDeviceModels, setSelectedDeviceModels] = useState([]);
  const [selectedDeviceTypeCategories, setSelectedDeviceTypeCategories] = useState([]);
  const [filteredDeviceTypeCategories, setFilteredDeviceTypeCategories] = useState([]);
  const [filteredDeviceModels, setFilteredDeviceModels] = useState([]);
  const [filteredOrganization, setFilteredOrganization] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [displaySearch, setDisplaySearch] = useState(false);

  const urlParams = useRef(null);
  const [initPhase, setInitPhase] = useState(0);

  useEffect(() => {
    setDisplaySearch(enableSearch);
  }, [enableSearch]);

  // Parse URL params once on mount
  useEffect(() => {
    if (!enableUrlPersistence) {
      setInitPhase(2);
      return;
    }
    const p = new URLSearchParams(location.search);
    const orgIds = p.get('orgs')?.split(',').filter(Boolean) || [];
    const typeIds = p.get('types')?.split(',').filter(Boolean) || [];
    const modelIds = p.get('models')?.split(',').filter(Boolean) || [];
    const search = p.get('search') || '';
    const page = parseInt(p.get('page')) || 1;

    if (orgIds.length || typeIds.length || modelIds.length || search) {
      urlParams.current = { orgIds, typeIds, modelIds, search, page };
    } else {
      setInitPhase(2);
    }
  }, []); // mount only

  // Phase 1: apply orgs when organizations prop arrives
  useEffect(() => {
    if (initPhase !== 0 || !urlParams.current) return;
    if (!organizations?.length) return;

    const { orgIds, typeIds, modelIds, search } = urlParams.current;
    const resolvedOrgs = organizations.filter((o) => orgIds.includes(String(o._id)));

    urlParams.current.resolvedOrgs = resolvedOrgs;
    setSelectedOrganizations(resolvedOrgs);
    if (search) setSearchText(search);

    if (orgIds.length || typeIds.length || modelIds.length) {
      props.onChangeOrg(resolvedOrgs.map((o) => o._id), []);
      setInitPhase(1);
    } else {
      // search-only: debounced searchText effect handles the filter call
      setInitPhase(2);
    }
  }, [organizations, initPhase]); // intentional: only re-run when organizations list changes

  // Phase 2: apply device types & models once parent loads them
  useEffect(() => {
    if (initPhase !== 1 || !urlParams.current) return;

    const { typeIds, modelIds, search, resolvedOrgs } = urlParams.current;
    // Only wait for device type/model data when we actually need to match stored IDs
    if ((typeIds.length > 0 || modelIds.length > 0) && !deviceTypeCategories?.length && !deviceModels?.length) return;

    const types = (deviceTypeCategories || []).filter((t) => typeIds.includes(String(t._id)));
    const models = (deviceModels || []).filter((m) => modelIds.includes(String(m._id)));

    setSelectedDeviceTypeCategories(types);
    setSelectedDeviceModels(models);
    setInitPhase(2);
    filterDataBasedOnSelection(resolvedOrgs, types, models, search, urlParams.current.page || 1);
  }, [initPhase, deviceTypeCategories, deviceModels]); // fires when phase becomes 1 OR when filter options arrive

  useEffect(() => {
    //Filtering the device type categories
    let sortedDeviceTypes = deviceTypeCategories;
    let sortedDeviceModels = deviceModels;
    if (deviceTypeCategories) {
      sortedDeviceTypes = deviceTypeCategories.sort((a, b) =>
        compareFunction("asc")(textTranslate(a.slug), textTranslate(b.slug)),
      );
    }
    if (deviceModels) {
      sortedDeviceModels = deviceModels.sort((a, b) => compareFunction("asc")(a.title, b.title));
    }
    setFilteredOrganization(organizations);
    setFilteredDeviceTypeCategories(sortedDeviceTypes);
    setFilteredDeviceModels(sortedDeviceModels);
  }, [deviceModels, deviceTypeCategories, organizations]);

  // Debounce search — skip during initialization to avoid stale-closure overwrites
  useEffect(() => {
    if (searchText === undefined) return;
    if (initPhase !== 2) return;
    const timer = setTimeout(() => {
      filterDataBasedOnSelection(selectedOrganizations, selectedDeviceTypeCategories, selectedDeviceModels, searchText);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchText]);

  const onChangeOrganizations = (selectedOptions) => {
    if (props.onChangeOrg) {
      const orgs = selectedOptions.map((org) => org._id);
      const deviceType = selectedDeviceTypeCategories.map((dt) => dt._id);

      props.onChangeOrg(orgs, deviceType);
    }
    setSelectedOrganizations(selectedOptions);
    filterDataBasedOnSelection(selectedOptions, selectedDeviceTypeCategories, selectedDeviceModels, searchText);
  };

  const onChangeDeviceTypeCategories = (selectedOptions) => {
    if (props.onChangeOrg) {
      const deviceType = selectedOptions.map((deviceType) => deviceType._id);
      // still want to get the org in array and pass it to the method

      let orgs = [""];
      if (selectedOrganizations.length > 0) {
        orgs = selectedOrganizations.map((org) => org._id);
      }

      props.onChangeOrg(orgs, deviceType);
    }

    setSelectedDeviceTypeCategories(selectedOptions);
    // filter device models based on device type category
    if (selectedOptions.length > 0) {
      const deviceModelIds = selectedOptions.map((item) => item._id);
      const filteredDeviceModels = deviceModels.filter((item) => deviceModelIds.includes(item.deviceTypeCategory._id));
      setFilteredDeviceModels(filteredDeviceModels);
    } else {
      setFilteredDeviceModels(deviceModels);
    }
    filterDataBasedOnSelection(selectedOrganizations, selectedOptions, selectedDeviceModels, searchText);
  };

  const onDeviceModelChange = (selectedOptions) => {
    setSelectedDeviceModels(selectedOptions);
    filterDataBasedOnSelection(selectedOrganizations, selectedDeviceTypeCategories, selectedOptions, searchText);
  };

  const onSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const filterDataBasedOnSelection = async (Organisation, DeviceTypeCategory, DeviceModel, searchBy, page = 1) => {
    let url = `${API_ENDPOINTS.DEVICES}?`;

    // dashboard and map is the two components where we dont want our data paginated
    if (
      window.location.pathname === "/dashboard" ||
      window.location.pathname === "/map" ||
      window.location.pathname === "/orders/service-orders/new-order"
    ) {
      url = url.concat("&perPage=10000");
    }

    if (Organisation && Organisation.length > 0) {
      const OrgId = Organisation.map((item) => item._id);
      url = url.concat(`&orgIds=${OrgId}`);
    }

    if (DeviceTypeCategory && DeviceTypeCategory.length > 0) {
      const DeviceTypeID = DeviceTypeCategory.map((item) => item._id);
      url = url.concat(`&deviceTypeCategoryIds=${DeviceTypeID}`);
    }

    if (DeviceModel && DeviceModel.length > 0) {
      const DeviceModelId = DeviceModel.map((item) => item._id);
      url = url.concat(`&deviceModelIds=${DeviceModelId}`);
    }

    if (searchText) {
      url = url.concat(`&search=${searchBy}`);
    }

    /**
     * If we are in either map, dashboard or serviceOrder and,
     *  no query params have been selected then don't fetch anything
     */

    if (
      window.location.pathname === "/dashboard" ||
      window.location.pathname === "/map" ||
      window.location.pathname === "/orders/service-orders/new-order"
    ) {
      if (!Organisation.length && !DeviceTypeCategory.length && !DeviceModel.length && !searchText) {
        props.onDevicesFiltering("");
        return;
      }
    }

    url = url.replace("?&", "?");

    if (enableUrlPersistence && history) {
      const p = new URLSearchParams(location.search); // preserve non-filter params (e.g. sort)
      if (Organisation?.length) p.set('orgs', Organisation.map((o) => o._id).join(','));
      else p.delete('orgs');
      if (DeviceTypeCategory?.length) p.set('types', DeviceTypeCategory.map((t) => t._id).join(','));
      else p.delete('types');
      if (DeviceModel?.length) p.set('models', DeviceModel.map((m) => m._id).join(','));
      else p.delete('models');
      if (searchBy) p.set('search', searchBy);
      else p.delete('search');
      history.replace({ search: p.toString() });
    }

    props.onDevicesFiltering(url, page);
  };

  const clearFilter = () => {
    if (enableUrlPersistence && history) {
      const p = new URLSearchParams(location.search); // preserve non-filter params (e.g. sort)
      p.delete('orgs');
      p.delete('types');
      p.delete('models');
      p.delete('search');
      history.replace({ search: p.toString() });
    }
    setSelectedDeviceModels([]);
    setSelectedDeviceTypeCategories([]);
    setSelectedOrganizations([]);
    setSearchText("");
    if (
      window.location.pathname === "/dashboard" ||
      window.location.pathname === "/map" ||
      window.location.pathname === "/orders/service-orders/new-order"
    ) {
      props.onDevicesFiltering("");
      props.onChangeOrg([""], [""]);
    } else {
      props.onDevicesFiltering("");
      props.onChangeOrg([""], [""]);
    }
  };

  const compareFunction = (order = "asc") => {
    return (a, b) => {
      if (order === "asc") {
        // Ascending order: smallest or alphabetically first
        return a > b ? 1 : a < b ? -1 : 0;
      } else if (order === "desc") {
        // Descending order: largest or alphabetically last
        return a > b ? -1 : a < b ? 1 : 0;
      }
      throw new Error("Invalid sort order. Use 'asc' or 'desc'.");
    };
  };

  return (
    <>
      {isFilterOpen ? (
        <>
          <Row className="mb-2 ">
            <Col sm="12" md={{ size: 6 }} className="clear-fix">
              <Button
                color="link"
                className="pull-left"
                onClick={() => {
                  setIsFilterOpen(false);
                }}
              >
                <i className="fa fa-minus mr-1" /> {textTranslate("hideAdvancedFiltering")}
              </Button>
            </Col>
            <Col sm="12" md={{ size: 6 }} className="mb-2 clear-fix">
              <Button color="info" className="pull-right" onClick={() => clearFilter()}>
                <i className="fa fa-eraser" /> {textTranslate("clearFilter")}
              </Button>
            </Col>
            {/* Organizations */}
            <Col sm="12" md="4" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("ownersLabel")}</Label>
              <Select
                onChange={onChangeOrganizations}
                options={filteredOrganization}
                value={selectedOrganizations}
                className="w-100"
                isMulti={true}
                autoFocus
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                placeholder={textTranslate("select")}
              />
            </Col>
            {/* Device types */}
            <Col sm="12" md="4" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("deviceTypesLabel")}</Label>
              <Select
                onChange={onChangeDeviceTypeCategories}
                options={filteredDeviceTypeCategories}
                value={selectedDeviceTypeCategories}
                className="w-100"
                isMulti={true}
                getOptionLabel={(option) => textTranslate(option.slug)}
                getOptionValue={(option) => option._id}
                sortByValue={(option) => option.name}
                placeholder={textTranslate("select")}
              />
            </Col>
            {/* Device models */}
            <Col sm="12" md="4" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("devicesModelsLabel")}</Label>
              <Select
                onChange={onDeviceModelChange}
                options={filteredDeviceModels}
                value={selectedDeviceModels}
                getOptionLabel={(option) => option.title}
                getOptionValue={(option) => option._id}
                className="w-100"
                isMulti={true}
                placeholder={textTranslate("select")}
              />
            </Col>
          </Row>
          <Row className="mb-2" hidden={!displaySearch}>
            <Col sm="12" md="12" className="clear-fix">
              <Input
                value={searchText}
                type="text"
                name="search"
                id="search"
                placeholder={textTranslate("search")}
                onChange={onSearchChange}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col sm="12" md={{ size: 6 }} className="clear-fix">
            <Button
              color="link"
              className="pull-left"
              onClick={() => {
                setIsFilterOpen(true);
              }}
            >
              <i className="fa fa-plus mr-1" /> {textTranslate("showAdvancedFiltering")}
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default withRouter(DeviceFiltering);
